import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useDesignPreviewStore } from '@/stores/design-preview-store';

interface ProductListParams {
  page?: number;
  limit?: number;
  category?: string;
  categories?: string[];
  search?: string;
  sort?: string;
  filters?: Record<string, string>;
}

export function useProducts(params: ProductListParams = {}) {
  const { page = 1, limit = 20, category, categories, search, sort, filters } = params;

  const queryString = new URLSearchParams();
  queryString.set('page', String(page));
  queryString.set('limit', String(limit));
  if (categories && categories.length > 0) {
    queryString.set('category', categories.join(','));
  } else if (category) {
    queryString.set('category', category);
  }
  if (search) queryString.set('search', search);
  if (sort) {
    const sortMap: Record<string, { sort: string; order: string }> = {
      newest: { sort: 'created_at', order: 'desc' },
      price_asc: { sort: 'price', order: 'asc' },
      price_desc: { sort: 'price', order: 'desc' },
      name_asc: { sort: 'name', order: 'asc' },
    };
    const mapped = sortMap[sort];
    if (mapped) {
      queryString.set('sort', mapped.sort);
      queryString.set('order', mapped.order);
    } else {
      queryString.set('sort', sort);
    }
  }
  if (filters) {
    for (const [key, value] of Object.entries(filters)) {
      queryString.set(key, value);
    }
  }

  return useQuery({
    queryKey: ['products', params],
    queryFn: () => api.get<{ products: any[]; total?: number; pagination?: { total: number; total_pages: number } }>(`/products?${queryString}`),
  });
}

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: () => api.get<any>(`/products/${slug}`),
    enabled: !!slug,
  });
}

export function useProductReviews(slug: string, page = 1, filters?: { sort?: string; rating?: string; verified?: string; q?: string }) {
  const params = new URLSearchParams({ page: String(page) });
  if (filters?.sort && filters.sort !== 'created_at') params.set('sort', filters.sort);
  if (filters?.rating) params.set('rating', filters.rating);
  if (filters?.verified === 'true') params.set('verified', 'true');
  if (filters?.q) params.set('q', filters.q);

  return useQuery({
    queryKey: ['product-reviews', slug, page, filters],
    queryFn: () => api.get<{ reviews: any[]; total: number; rating_distribution: any[]; attribute_averages: any[]; pagination: any }>(`/products/${slug}/reviews?${params}`),
    enabled: !!slug,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<{ categories: any[] }>('/categories'),
  });
}

export function useCategory(slug: string) {
  return useQuery({
    queryKey: ['category', slug],
    queryFn: () => api.get<any>(`/categories/${slug}`),
    enabled: !!slug,
  });
}

export function useSearch(query: string, params: Record<string, string> = {}) {
  const queryString = new URLSearchParams({ q: query, ...params });
  return useQuery({
    queryKey: ['search', query, params],
    // The full-text path answers with `pagination.total`; `total` is kept for the instant-search shape.
    queryFn: () =>
      api.get<{ products: any[]; total?: number; pagination?: { total: number } }>(`/search?${queryString}`),
    enabled: query.length >= 2,
  });
}

const HOMEPAGE_CACHE_KEY = 'rumio-homepage-cache';
const SETTINGS_CACHE_KEY = 'rumio-settings-cache';

function readCache<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(key: string, data: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {}
}

export function useHomepage() {
  return useQuery({
    queryKey: ['homepage'],
    queryFn: async () => {
      const data = await api.get<{ sections: any[] }>('/homepage');
      writeCache(HOMEPAGE_CACHE_KEY, data);
      return data;
    },
    placeholderData: () => readCache(HOMEPAGE_CACHE_KEY),
  });
}

export function useSettings() {
  // Design Sandbox live preview: inside a `?design_preview=1` storefront iframe,
  // DesignPreviewBridge populates this store and we substitute the candidate
  // theme_config for the live one. Every consumer (PublicLayout, Header,
  // HeroSection, Footer) reads this hook, so the whole storefront re-renders
  // with the candidate — for this frame's session only. Null in normal sessions
  // (the store starts null and only the bridge ever sets it), so this is a
  // no-op everywhere except the preview frame.
  const previewThemeConfig = useDesignPreviewStore((s) => s.previewThemeConfig);
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const data = await api.get<{ settings: Record<string, any> }>('/settings/public');
      writeCache(SETTINGS_CACHE_KEY, data);
      return data;
    },
    staleTime: 1000 * 60 * 30,
    placeholderData: () => readCache(SETTINGS_CACHE_KEY),
    select: (data) =>
      previewThemeConfig
        ? { ...data, settings: { ...data.settings, theme_config: previewThemeConfig } }
        : data,
  });
}

export function useBlogPosts(page = 1, category?: string) {
  const params = new URLSearchParams({ page: String(page) });
  if (category) params.set('category', category);
  return useQuery({
    queryKey: ['blog', page, category],
    queryFn: () => api.get<{ posts: any[]; total: number }>(`/blog?${params}`),
  });
}

export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => api.get<any>(`/blog/${slug}`),
    enabled: !!slug,
  });
}

export function useBlogCategories() {
  return useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.get<{ categories: any[] }>('/blog/categories'),
  });
}

export function usePage(slug: string) {
  return useQuery({
    queryKey: ['page', slug],
    queryFn: () => api.get<any>(`/pages/${slug}`),
    enabled: !!slug,
  });
}

export function useOrders(page = 1) {
  return useQuery({
    queryKey: ['orders', page],
    queryFn: () => api.get<{ orders: any[]; total: number }>(`/orders?page=${page}`),
  });
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: ['order', id],
    queryFn: () => api.get<any>(`/orders/${id}`),
    enabled: !!id,
  });
}

export function useWishlist() {
  return useQuery({
    queryKey: ['wishlist'],
    queryFn: () => api.get<{ items: any[] }>('/wishlist'),
  });
}

export function useShippingRates(state?: string, orderAmount?: number) {
  const params = new URLSearchParams();
  if (state) params.set('state', state);
  if (orderAmount) params.set('order_amount', String(orderAmount));
  return useQuery({
    queryKey: ['shipping-rates', state, orderAmount],
    queryFn: () => api.get<{ rates: any[]; free_shipping_eligible: boolean }>(`/shipping/rates?${params}`),
    enabled: !!state,
  });
}
